import { useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { de } from "react-day-picker/locale";
//Dont change the order of these imports necessary for css order!
//@ts-ignore doesnt need types
import classNames from "react-day-picker/style.module.css";
import { format, isValid, parse } from "date-fns";

import { TextInput } from "../..";
import { InputType } from "../Input/TextInput";

import styles from "./DatePicker.scss";

interface DatePickerProps {
  placeholder: string;
  minDate: Date;
  maxDate: Date;
  dateFormat: string;
  input: InputType;
}

function DatePicker({ input, placeholder, minDate, maxDate, dateFormat }: DatePickerProps) {
  const mainRef = useRef<HTMLDivElement>(null);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const prevShowDatePicker = useRef<boolean>(false);
  const prevInputValue = useRef<string>(input.value);

  // Track the currently displayed month separately from the input value
  // This allows the calendar to navigate to different months/years via dropdowns
  const parsedDate = parse(input.value, dateFormat, new Date());
  const checkedDate = isValid(parsedDate) ? parsedDate : new Date();
  const [displayMonth, setDisplayMonth] = useState<Date>(checkedDate);

  const handleDayPickerDayClick = (date: Date | undefined) => {
    setShowDatePicker(false);
    if (date) {
      input.onChange(format(date, dateFormat));
    }
  };

  const handleMonthChange = (newMonth: Date) => {
    setDisplayMonth(newMonth);
  };

  // Reset displayed month ONLY when:
  // 1. Picker opens (transition from closed to open)
  // 2. Input value changes while picker is open
  useEffect(() => {
    const justOpened = showDatePicker && !prevShowDatePicker.current;
    const inputChanged = input.value !== prevInputValue.current;

    if (justOpened || (showDatePicker && inputChanged)) {
      setDisplayMonth(checkedDate);
    }

    prevShowDatePicker.current = showDatePicker;
    prevInputValue.current = input.value;
  }, [showDatePicker, input.value, checkedDate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    input.onChange(e.target.value);
  };

  useEffect(() => {
    function handleOutSideClick(e: MouseEvent) {
      if (mainRef.current && !mainRef.current.contains(e.target as Node)) {
        setShowDatePicker(false);
      }
    }

    document.addEventListener("mousedown", handleOutSideClick);
    return () => document.removeEventListener("mousedown", handleOutSideClick);
  }, []);

  return (
    <div ref={mainRef}>
      <TextInput
        input={input}
        meta={{}}
        placeholder={placeholder}
        onChange={handleInputChange}
        onFocus={() => setShowDatePicker(true)}
        sideSymbol={() => (
          <i className="icon-calendar-empty" onClick={() => setShowDatePicker(!showDatePicker)} />
        )}
      />
      {showDatePicker && (
        <DayPicker
          locale={de}
          classNames={classNames}
          month={displayMonth}
          onMonthChange={handleMonthChange}
          mode="single"
          selected={checkedDate}
          onDayClick={handleDayPickerDayClick}
          captionLayout="dropdown"
          className={styles.overrides}
          disabled={{ before: minDate, after: maxDate }}
          startMonth={minDate}
          endMonth={maxDate}
          data-testId="dayPickerCard"
        />
      )}
    </div>
  );
}

export default DatePicker;
